import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getSSOParams, disconnectErp, setCurrentERPSettings, activateErpAction } from 'domain/settings/settingsActions';
import { availableErpsForCompanySelector, configurationCompanySelector } from 'domain/companies/companiesSelector';
import { canErpSyncSelector, erpsSelector, isLoadingERPSettingSelector } from 'domain/settings/settingsSelector';
import { companyFeatureSetSelector } from 'domain/companies';
import { ERPOptionsAdapter, Statuses, sortingStatusOrderCb, statusesTitle } from './helpers';
import { ERP_NAMES, ERP_TITLES } from 'domain/companies/helpers';
import { queryToSearch } from 'domain/router/helpers';
import queryString from 'query-string';
import Api from 'domain/api';
import { documentInvalidateMRUTagsCacheAction } from 'domain/documents';
import { FormattedMessage } from 'react-intl';
import { ERPFactory } from 'domain/settings/adapters';
import useToggle from 'hooks/useToggle';

import MuiAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import toast from 'components/Toast';
import MuiButton from '@mui/material/Button';
import MuiChip from '@mui/material/Chip';
import MuiLoadingButton from '@mui/lab/LoadingButton';
import Dialog from '@mui/material/Dialog';
import { red } from '@mui/material/colors';
import ZohoConnectModal from './components/ZohoConnectModal';
import AddMoreCompaniesSection from './components/ERPConnectForm/AddMoreCompaniesSection';
import ArrowRightIcon from 'pages/components/AccordionArrowRightIcon';
import ERPConnectModal from './components/ERPConnectModal';
import ERPSettings from 'components/ERPSettings';
import SyncButton from './components/SyncButton';
import CircularProgressWithBackdrop from 'components/mui/CircularProgressWithBackdrop';
import { useConfirm } from 'material-ui-confirm';

import qboIcon from './icons/qboIcon.png';
import xeroIcon from './icons/xeroIcon.png';
import zohoIcon from './icons/zohoIcon.svg';
import netsuiteIcon from './icons/netsuiteIcon.png';
import sapIcon from './icons/sapIcon.png';
import sageSaIcon from './icons/sageIcon.svg';
import dokkaIcon from './icons/dokkaIcon.png';
import priorityIcon from './icons/priorityIcon.svg';
import qbd from './icons/qbd.png';
import hashavshevetIcon from './icons/hashavshevetIcon.jpeg';

import cx from 'classnames';
import { styled, useTheme } from '@mui/material/styles';
import { useStyles } from './styled';

const Chip = styled(MuiChip)(() => ({
  borderRadius: 4,
}));

const createStatuses = (item) => ({
  [Statuses.connected]: (
    <Chip
      color="success"
      label={
        <FormattedMessage id="settings.connections.connected" defaultMessage="Connected">
          {(t) =>
            item.settings.getIn(['details', 'company_name'])
              ? `${t} (${item.settings.getIn(['details', 'company_name'])})`
              : t
          }
        </FormattedMessage>
      }
      size="small"
    />
  ),
  [Statuses.inactive]: (
    <Chip
      variant="outlined"
      color="error"
      label={
        <FormattedMessage id="settings.connections.inactive" defaultMessage="Inactive">
          {(t) =>
            item.settings.getIn(['details', 'company_name'])
              ? `${t} (${item.settings.getIn(['details', 'company_name'])})`
              : t
          }
        </FormattedMessage>
      }
      size="small"
      sx={() => ({ backgroundColor: red[100] })}
    />
  ),
  [Statuses.authorized]: (
    <Chip
      color="warning"
      sx={(theme) => ({ backgroundColor: theme.palette.warning.light })}
      label={<FormattedMessage id="settings.connections.authorized" defaultMessage="Authorized" />}
      size="small"
    />
  ),
  [Statuses.connecting]: (
    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
      <Chip
        color="warning"
        label={<FormattedMessage id="settings.connections.connecting" defaultMessage="Connecting" />}
        size="small"
        sx={(theme) => ({ backgroundColor: theme.palette.warning.light })}
      />
      <Typography variant="subtitle2" sx={(theme) => ({ color: theme.palette.warning.light })}>
        <FormattedMessage
          id="settings.connections.connectingMsg"
          defaultMessage="Process might take few minutes.
                          Reload the page to check the connection status"
        />
      </Typography>
    </Stack>
  ),
});

const Accordion = styled(MuiAccordion)(({ theme }) => ({
  marginBottom: 1.5,
  marginTop: '12px !important',
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: 'none',

  '&.MuiPaper-root.MuiAccordion-root::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary elevation={0} expandIcon={<ArrowRightIcon />} {...props} />
))(() => ({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-content': {
    margin: 0,
    height: 64,
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: 0,
  },
}));

const Button = ({ onClick, ...rest }) => (
  <MuiButton
    {...rest}
    onClick={(...args) => {
      args[0].stopPropagation();
      if (typeof onClick === 'function') onClick(...args);
    }}
  />
);
const LoadingButton = ({ onClick, ...rest }) => (
  <MuiLoadingButton
    {...rest}
    onClick={(...args) => {
      args[0].stopPropagation();
      if (typeof onClick === 'function') onClick(...args);
    }}
  />
);

const mapStateToProps = (state) => ({
  availableErps: availableErpsForCompanySelector(state),
  configurationCompany: configurationCompanySelector(state),
  erps: erpsSelector(state),
  sslVerification: companyFeatureSetSelector(state).get('erp_ssl_verification'),
  isLoadingERPSetting: isLoadingERPSettingSelector(state),
  canErpSync: canErpSyncSelector(state),
});

const Integrations = () => {
  const [openedSection, setOpenedSection] = useState(null);
  const [connectModal, setConnectModal] = useState(null);
  const [isBusy, toggleIsBusy] = useToggle(null);

  const { availableErps, erps, sslVerification, isLoadingERPSetting, configurationCompany, canErpSync } =
    useSelector(mapStateToProps);
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();

  const confirm = useConfirm();

  const defaultErpStatus = erps.getIn([ERP_NAMES.defaultErp, 'status']);

  const handleSectionClick = (section) => (e) => {
    setOpenedSection(openedSection === section ? null : section);
  };

  const openConnectModal = useCallback((id) => {
    setConnectModal(id);
  }, []);

  const handleConnectModalClose = useCallback(() => {
    setConnectModal(null);
  }, []);

  const onDisconnectClick = useCallback(
    (title) => {
      confirm({
        title: <FormattedMessage id="settings.modals.disconnect.confirm.title" defaultMessage="Disconnect company" />,
        description: (
          <FormattedMessage
            id="settings.modals.disconnect.confirm.message"
            defaultMessage="Are you sure you want to disconnect?"
            values={{
              labelName: theme.labelOptions.name.toUpperCase(),
              erpName: title,
              companyName: configurationCompany.name,
            }}
          />
        ),
        confirmationText: (
          <FormattedMessage id="settings.modals.disconnect.confirm.acceptBtn" defaultMessage="Disconnect" />
        ),
        confirmationButtonProps: {
          color: 'error',
        },
      }).then(() => {
        dispatch(disconnectErp());
      });
    },
    [configurationCompany.name, confirm, dispatch, theme.labelOptions.name],
  );

  const onConnect = useCallback(
    (erpType, configs) => {
      new Promise((resolve, reject) => dispatch(getSSOParams({ erpType, configs, resolve, reject })))
        .then((response) => {
          const paramsExist = Boolean(Object.keys(response.params).length);
          window.location.href = `${response.endpoint}${paramsExist ? `?${queryToSearch(response.params)}` : ''}`;
        })
        .catch(() => null);
    },
    [dispatch],
  );

  const getFormData = useCallback((object) => {
    const formData = new FormData();
    Object.keys(object).forEach((key) => formData.append(key, object[key]));
    return formData;
  }, []);

  const handleSageError = useCallback(() => {
    const queryParams = queryString.parse(window.location.search);
    if (queryParams.error) {
      toast.error(queryParams.error);
    }
  }, []);

  // clear cache data related to ERP settings.
  const invalidateCacheData = useCallback(() => {
    // clear cached company tags
    dispatch(documentInvalidateMRUTagsCacheAction());
  }, [dispatch]);

  const handleSubmit = useCallback(
    (erp: string, data: Map<string, string>) => {
      const { propagate = {}, ...config } = data.toJS();
      toggleIsBusy(true);

      return new Promise((resolve, reject) => {
        dispatch(
          setCurrentERPSettings({
            data: {
              config,
              erp_type: erp.toUpperCase(),
              ...propagate,
            },
            resolve,
            reject,
          }),
        );
      })
        .then(invalidateCacheData)
        .finally(() => toggleIsBusy(false));
    },
    [dispatch, invalidateCacheData],
  );

  const getIgnoreSSlFieldSet = useCallback(
    () =>
      sslVerification
        ? [
            {
              name: 'ssl',
              type: 'checkbox',
              xs: 12,
              label: {
                id: 'erpConnect.verifySSL',
                defaultMessage: 'Verify SSL Certificate',
              },
            },
          ]
        : [],
    [sslVerification],
  );

  const getIsConnected = useCallback(
    (settings) => [Statuses.connected, Statuses.authorized].some((i) => i === settings.status),
    [],
  );

  const erpsOptions = useMemo(
    () => [
      {
        section: ERP_NAMES.qbo,
        title: ERP_TITLES[ERP_NAMES.qbo],
        erpName: 'QuickBooks Online',
        icon: qboIcon,
        button: (
          <Button className={cx(classes.connectBtn, classes.qboButton)} onClick={() => onConnect(ERP_NAMES.qbo)} />
        ),
        settings: erps.get(ERP_NAMES.qbo, ERPFactory()),
      },
      {
        section: ERP_NAMES.qbd,
        title: ERP_TITLES[ERP_NAMES.qbd],
        erpName: 'QuickBooks Desktop',
        icon: qbd,
        settings: erps.get(ERP_NAMES.qbd, ERPFactory()),
      },
      {
        section: ERP_NAMES.xero,
        title: ERP_TITLES[ERP_NAMES.xero],
        erpName: 'XERO',
        icon: xeroIcon,
        button: (
          <Button
            className={cx(classes.connectBtn, classes.xeroButton)}
            type="button"
            onClick={() => onConnect(ERP_NAMES.xero)}
          />
        ),
        settings: erps.get(ERP_NAMES.xero, ERPFactory()),
      },
      {
        section: ERP_NAMES.zoho,
        title: ERP_TITLES[ERP_NAMES.zoho],
        erpName: 'Zoho Books',
        icon: zohoIcon,
        button: (
          <Button
            className={cx(classes.connectBtn, classes.zohoButton)}
            type="button"
            onClick={() => onConnect(ERP_NAMES.zoho)}
          />
        ),
        settings: erps.get(ERP_NAMES.zoho, ERPFactory()),
      },
      {
        section: ERP_NAMES.sageSA,
        title: ERP_TITLES[ERP_NAMES.sageSA],
        erpName: 'Sage Business Cloud (South Africa)',
        icon: sageSaIcon,
        erpConnect: {
          fields: [
            {
              name: 'username',
              type: 'text',
              xs: 12,
              label: {
                id: 'sageConnect.username',
                defaultMessage: 'Username',
              },
            },
            {
              name: 'password',
              type: 'password',
              xs: 12,
              label: {
                id: 'sageConnect.password',
                defaultMessage: 'Password',
              },
            },
          ],
          api: Api.signInSage,
          apiPayloadSerializer: (formData, dokkaToken) => ({ data: { dokkaToken, ...formData.toJS() } }),
        },
        settings: erps.get(ERP_NAMES.sageSA, ERPFactory()),
      },
      {
        section: ERP_NAMES.sage,
        title: ERP_TITLES[ERP_NAMES.sage],
        erpName: 'Sage Business Cloud (Global)',
        icon: sageSaIcon,
        button: (
          <Button
            className={cx(classes.connectBtn, classes.sageGlobalButton)}
            onClick={() => onConnect(ERP_NAMES.sage)}
          />
        ),
        settings: erps.get(ERP_NAMES.sage, ERPFactory()),
      },
      {
        section: ERP_NAMES.priorityPro,
        title: ERP_TITLES[ERP_NAMES.priorityPro],
        erpName: 'Priority',
        icon: priorityIcon,
        erpConnect: {
          fields: [
            {
              name: 'username',
              type: 'text',
              label: {
                id: 'sageConnect.username',
                defaultMessage: 'Username',
              },
            },
            {
              name: 'password',
              type: 'password',
              label: {
                id: 'sageConnect.password',
                defaultMessage: 'Password',
              },
            },
            {
              name: 'apiUrl',
              type: 'text',
              xs: 12,
              label: {
                id: 'priorityConnect.apiUrl',
                defaultMessage: 'API Url',
              },
            },
            ...getIgnoreSSlFieldSet(),
          ],
          api: Api.signInPriority,
          apiPayloadSerializer: (formData, dokkaToken) => ({ data: { dokkaToken, ...formData.toJS() } }),
          onSuccessToastMessageProps: {
            id: 'priorityConnect.allWillConnect',
            defaultMessage: 'Selected companies will be connected to {label} Please, refresh company list',
          },
        },
        settings: erps.get(ERP_NAMES.priorityPro, ERPFactory()),
      },
      {
        section: ERP_NAMES.priorityProSQL,
        title: ERP_TITLES[ERP_NAMES.priorityProSQL],
        icon: priorityIcon,
        settings: erps.get(ERP_NAMES.priorityProSQL, ERPFactory()),
      },
      {
        section: ERP_NAMES.fox,
        title: ERP_TITLES[ERP_NAMES.fox],
        erpName: 'Custom ERP',
        icon: dokkaIcon,
        button: <div />,
        settings: erps.get(ERP_NAMES.fox, ERPFactory()),
      },
      {
        section: ERP_NAMES.hash,
        title: ERP_TITLES[ERP_NAMES.hash],
        icon: hashavshevetIcon,
        erpName: 'Hashavshevet',
        erpConnect: {
          api: Api.connectHashDevice,
          apiPayloadSerializer: (formData, dokkaToken) => ({ data: { dokkaToken, ...formData } }),
          onSuccessToastMessageProps: {
            id: 'priorityConnect.allWillConnect',
            defaultMessage: 'Selected companies will be connected to {label} Please, refresh company list',
          },
        },
        settings: erps.get(ERP_NAMES.hash, ERPFactory()),
      },
      // TODO: should be changed after back-end is ready DAW-1575
      {
        section: ERP_NAMES.netsuite,
        title: ERP_TITLES[ERP_NAMES.netsuite],
        icon: netsuiteIcon,
        erpName: 'NETSUITE',
        erpConnect: {
          fields: [
            {
              name: 'accountId',
              type: 'text',
              label: {
                id: 'netsuiteConnect.accountId',
                defaultMessage: 'Account Id',
              },
            },
            {
              name: 'tokenKey',
              type: 'text',
              label: {
                id: 'netsuiteConnect.tokenKey',
                defaultMessage: 'Token Key',
              },
            },
            {
              name: 'tokenSecret',
              type: 'password',
              label: {
                id: 'netsuiteConnect.accountSecret',
                defaultMessage: 'Token  Secret',
              },
            },
            {
              name: 'restUrl',
              type: 'text',
              label: {
                id: 'netsuiteConnect.restUrl',
                defaultMessage: 'Rest connector URL',
              },
            },
          ],
          api: Api.signInNetSuite,
          apiPayloadSerializer: (formData, dokkaToken) => ({
            data: {
              dokkaToken,
              ...formData.toJS(),
            },
          }),
          onSuccessToastMessageProps: { id: 'netsuiteConnect.toast', defaultMessage: 'NetSuite credentials sent' },
          isSingleStep: true,
        },
        settings: erps.get(ERP_NAMES.netsuite, ERPFactory()),
      },
      {
        section: ERP_NAMES.sap,
        title: ERP_TITLES[ERP_NAMES.sap],
        icon: sapIcon,
        erpName: 'SAP Business One',
        erpConnect: {
          section: {
            element: (fields) => <AddMoreCompaniesSection fields={fields} />,
            fields: [
              {
                name: 'companies_excel',
                label: {
                  id: 'sapConnect.uploadCompaniesExcel',
                  defaultMessage: 'Upload companies excel',
                },
              },
            ],
            onSuccessToastMessageProps: {
              id: 'sapConnect.section.toast.success',
              defaultMEssages: 'Selected companies will be connected to DOKKA. Please, refresh company list',
            },
          },
          fields: [
            {
              name: 'username',
              type: 'text',
              label: {
                id: 'sapConnect.username',
                defaultMessage: 'SAP username',
              },
            },
            {
              name: 'password',
              type: 'password',
              label: {
                id: 'sapConnect.password',
                defaultMessage: 'SAP password',
              },
            },
            {
              name: 'CompanyDB',
              type: 'text',
              label: {
                id: 'sapConnect.companyDB',
                defaultMessage: 'Company DB name',
              },
            },
            {
              name: 'api_url',
              type: 'text',
              label: {
                id: 'sapConnect.apiUrl',
                defaultMessage: 'SAP server url',
              },
            },
            ...getIgnoreSSlFieldSet(),
          ],
          api: Api.signInSAP,
          apiPayloadSerializer: (formData, dokkaToken) => ({
            data: getFormData({ ...formData.toJS(), dokkaToken }),
          }),
          onSuccessToastMessageProps: { id: 'sapConnect.toast.success', defaultMessage: 'SAP credentials sent' },
          isSingleStep: true,
        },
        settings: erps.get(ERP_NAMES.sap, ERPFactory()),
      },
      {
        section: ERP_NAMES.defaultErp,
        title: 'Export to Excel/CSV only',
        icon: dokkaIcon,
        erpName: 'Default ERP',
        button: defaultErpStatus === 'inactive' && (
          <Button onClick={() => dispatch(activateErpAction(ERP_NAMES.defaultErp))}>
            <FormattedMessage id="settings.connections.btn.activate" defaultMessage="Activate" />
          </Button>
        ),
        settings: erps.get(ERP_NAMES.defaultErp, ERPFactory()),
      },
    ],
    [
      classes.connectBtn,
      classes.qboButton,
      classes.sageGlobalButton,
      classes.xeroButton,
      classes.zohoButton,
      defaultErpStatus,
      dispatch,
      erps,
      getFormData,
      getIgnoreSSlFieldSet,
      onConnect,
    ],
  );

  const renderButton = useCallback(
    ({ settings, button, section, erpConnect, option }) => {
      const isConnected = getIsConnected(settings);

      const initButtons = () => {
        switch (section) {
          case ERP_NAMES.defaultErp:
            switch (settings.status) {
              case Statuses.inactive:
                return (
                  <LoadingButton
                    variant="contained"
                    loading={settings.status === 'connecting'}
                    onClick={() => dispatch(activateErpAction(section))}
                  >
                    <FormattedMessage id="settings.connections.btn.activate" defaultMessage="Activate" />
                  </LoadingButton>
                );
              default:
                return null;
            }

          default:
            switch (true) {
              case ERP_NAMES.qbd === section && !isConnected:
                return (
                  <LoadingButton
                    variant="contained"
                    loading={settings.status === 'connecting'}
                    onClick={() => dispatch(activateErpAction(section))}
                  >
                    <FormattedMessage id="settings.connections.btn.activate" defaultMessage="Activate" />
                  </LoadingButton>
                );
              case isConnected: {
                return (
                  <Button variant="text" color="error" onClick={() => onDisconnectClick(option.title)}>
                    <FormattedMessage id="settings.connections.disconnect" defaultMessage="Disconnect" />
                  </Button>
                );
              }
              case !!button: {
                return button;
              }
              case !!erpConnect: {
                return (
                  <LoadingButton
                    variant="contained"
                    loading={settings.status === 'connecting'}
                    onClick={() => {
                      openConnectModal(option);
                    }}
                  >
                    <FormattedMessage defaultMessage="Connect" id="settings.connections.connect" />
                  </LoadingButton>
                );
              }
              default: {
                return null;
              }
            }
        }
      };
      return initButtons();
    },
    [dispatch, onDisconnectClick, openConnectModal, getIsConnected],
  );

  const options = useMemo(() => ERPOptionsAdapter(availableErps)(erpsOptions), [availableErps, erpsOptions]);

  useEffect(() => {
    handleSageError();
  }, [handleSageError]);

  return (
    <Stack flex={0}>
      {Object.entries(options)
        .sort(sortingStatusOrderCb)
        .map(([name, options]) => (
          <Stack key={name} justifyContent="start">
            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              <FormattedMessage {...statusesTitle[name]} />
            </Typography>
            {options.map((option) => {
              const { title, settings, section, erpConnect, erpName, button, icon } = option;
              const isConnected = getIsConnected(settings);

              return (
                <Accordion key={title} expanded={section === openedSection} onChange={handleSectionClick(section)}>
                  <AccordionSummary>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      flex="1"
                      flexWrap="no-wrap"
                    >
                      <Stack display="flex" direction="row" spacing={1} alignItems="center">
                        <Stack
                          justifyContent="center"
                          alignItems="center"
                          sx={(theme) => ({
                            padding: theme.spacing(0.5, 1),
                            border: 1,
                            borderColor: theme.palette.grey[200],
                            backgroundColor: theme.palette.grey[100],
                            borderRadius: 2,
                            minHeight: 34,
                          })}
                        >
                          <img width={24} height="auto" src={icon} alt="icon" />
                        </Stack>
                        <Typography>{[title]}</Typography>
                        {createStatuses(option)[settings.status]}
                      </Stack>
                      <Stack direction="row" spacing={2} height="100%">
                        {isConnected && canErpSync && <SyncButton />}
                        {isConnected && renderButton({ settings, button, section, erpConnect, option })}
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  {isConnected ? (
                    <AccordionDetails sx={{ p: 2 }}>
                      <ERPSettings
                        onSubmit={(data) => handleSubmit(section, data)}
                        erp={section}
                        erpName={erpName}
                        isBusy={isLoadingERPSetting || isBusy}
                      />
                    </AccordionDetails>
                  ) : (
                    <AccordionDetails sx={{ p: 2, pl: 5, pt: 0 }}>
                      {renderButton({ settings, button, section, erpConnect, option })}
                    </AccordionDetails>
                  )}
                  {option.settings.get('status') === 'authorized' && <ZohoConnectModal />}
                </Accordion>
              );
            })}
          </Stack>
        ))}
      {connectModal && (
        <Dialog open={!!connectModal} onClose={handleConnectModalClose} fullWidth>
          <ERPConnectModal
            handleConnectModal={handleConnectModalClose}
            erp={connectModal.section}
            {...connectModal.erpConnect}
          />
        </Dialog>
      )}
      {(isLoadingERPSetting || isBusy) && <CircularProgressWithBackdrop />}
    </Stack>
  );
};

export default Integrations;
